import { createContext, useRef, useState } from "react";

import {
    AppsContainerModel,
    AppsCreateMode,
    AppsEditMode,
    AppsModalContent,
    EditModalDataType,
} from "types/apps-container";
import { AppsContainerType } from "./apps-container-tab-context-types";
import { DataGrid } from "devextreme-react";

import {
    getEnvVariable,
    REACT_APP_PORTAL_APPS_ADMIN,
    REACT_APP_PORTAL_IMPLANT_ROLE,
} from "utils/environment";
import { useKeycloakSelector } from "redux/reducers/keycloak/hooks/useKeycloakSelector.hook";
import { useRequestStatusManager } from "utils/hooks/use-request-status-manager.hook";

const initialValue: AppsContainerType = {
    containers: {},
    modalContent: null,
    editModalData: [],
    editModalGroupName: "",
    editMode: "group",
    createMode: "group",
    createSubgroupTarget: "",
    containerId: null,
    isAdminUser: false,
    isImplantUser: false,
    editTableRef: { current: null },
    allContentIsReady: true,
    errorList: [],
    success: false,
    editActive: false,
    setEditActive: () => {},
    setError: () => {},
    unsubscribeContent: () => {},
    subscribeContent: () => {},
    removeAllData: () => {},
    setContainerId: () => {},
    setCreateMode: () => {},
    setCreateSubgroupTarget: () => {},
    setContainers: () => {},
    setEditModalData: () => {},
    setEditModalGroupName: () => {},
    setModalContent: () => {},
    setEditMode: () => {},
};

const implantRole = getEnvVariable(REACT_APP_PORTAL_IMPLANT_ROLE);
const adminRole = getEnvVariable(REACT_APP_PORTAL_APPS_ADMIN);

export const appsContainerTabContext = createContext(initialValue);

export const AppsContainerTabContextProvider: React.FC = ({ children }) => {
    const [containers, setContainers] = useState<AppsContainerModel>({});
    const [modalContent, setModalContent] = useState<AppsModalContent>(null);
    const [editModalData, setEditModalData] = useState<EditModalDataType[]>([]);
    const [editModalGroupName, setEditModalGroupName] = useState("");
    const [editMode, setEditMode] = useState<AppsEditMode>("group");
    const [createMode, setCreateMode] = useState<AppsCreateMode>("group");
    const [createSubgroupTarget, setCreateSubgroupTarget] = useState("");
    const [containerId, setContainerId] = useState<number | null>(null);
    const [editActive, setEditActive] = useState(false);

    const { userRoles } = useKeycloakSelector();

    const editTableRef = useRef<DataGrid<EditModalDataType>>(null);

    const {
        allContentIsReady,
        errorList,
        success,
        setError,
        unsubscribeContent,
        subscribeContent,
        removeAllData,
    } = useRequestStatusManager();

    const checkIsImplantUser = () => userRoles.includes(implantRole);
    const checkIsAdminUser = () => userRoles.includes(adminRole);

    return (
        <appsContainerTabContext.Provider
            value={{
                containers,
                modalContent,
                editModalData,
                editModalGroupName,
                editMode,
                createMode,
                createSubgroupTarget,
                containerId,
                editTableRef,
                isAdminUser: checkIsAdminUser(),
                isImplantUser: checkIsImplantUser(),
                allContentIsReady,
                errorList,
                success,
                editActive,
                setEditActive,
                setError,
                unsubscribeContent,
                subscribeContent,
                removeAllData,
                setContainerId,
                setCreateMode,
                setCreateSubgroupTarget,
                setContainers,
                setEditModalData,
                setEditModalGroupName,
                setModalContent,
                setEditMode,
            }}
        >
            {children}
        </appsContainerTabContext.Provider>
    );
};
